import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useGaContext } from "@/hooks/useGaContext";
import {
  Mail,
  Plus,
  X,
  Save,
  Users,
  Info,
  Send,
  UserCheck,
  Edit3,
  Check,
} from "lucide-react";

function EmailChip({ email, onRemove }: { email: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-full px-3 py-1 text-sm">
      <Mail className="w-3 h-3 shrink-0" />
      {email}
      <button
        onClick={onRemove}
        className="text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 ml-0.5"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function BrokerRepRow({
  broker,
  assignment,
  onSave,
}: {
  broker: any;
  assignment: any;
  onSave: (brokerAccountId: string, repName: string, repEmail: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [repName, setRepName] = useState(assignment?.salesRepName || "");
  const [repEmail, setRepEmail] = useState(assignment?.salesRepEmail || "");

  useEffect(() => {
    setRepName(assignment?.salesRepName || "");
    setRepEmail(assignment?.salesRepEmail || "");
  }, [assignment]);

  const hasAssignment = assignment?.salesRepEmail;

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-3 px-4">
        <p className="text-sm font-medium text-foreground">{broker.brokerAgencyName || broker.brokerAccountId}</p>
        {broker.brokerContactEmail && (
          <p className="text-xs text-muted-foreground mt-0.5">{broker.brokerContactEmail}</p>
        )}
      </td>
      <td className="py-3 px-4">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              className="h-7 text-xs w-36"
              placeholder="Rep name"
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
            />
            <Input
              className="h-7 text-xs w-48"
              placeholder="rep@agency.com"
              value={repEmail}
              onChange={(e) => setRepEmail(e.target.value)}
            />
            <Button
              size="sm"
              className="h-7 px-2"
              onClick={() => {
                onSave(broker.brokerAccountId, repName, repEmail);
                setEditing(false);
              }}
            >
              <Check className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => {
                setRepName(assignment?.salesRepName || "");
                setRepEmail(assignment?.salesRepEmail || "");
                setEditing(false);
              }}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {hasAssignment ? (
              <div>
                <p className="text-sm font-medium text-foreground">{assignment.salesRepName || "—"}</p>
                <p className="text-xs text-muted-foreground">{assignment.salesRepEmail}</p>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground italic">Not assigned</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setEditing(true)}
            >
              <Edit3 className="w-3 h-3 mr-1" />
              {hasAssignment ? "Edit" : "Assign"}
            </Button>
          </div>
        )}
      </td>
      <td className="py-3 px-4">
        <span className={`text-xs font-medium ${
          broker.status === "active" ? "text-green-600 dark:text-green-400" :
          broker.status === "pending" ? "text-amber-500 dark:text-amber-400" : "text-muted-foreground"
        }`}>
          {broker.status === "pending" ? "Invited" : broker.status === "active" ? "Active" : "Revoked"}
        </span>
      </td>
    </tr>
  );
}

export default function GAApiConnections() {
  const { gaId } = useGaContext();
  const { toast } = useToast();

  const [emailInput, setEmailInput] = useState("");
  const [ccEmailInput, setCcEmailInput] = useState("");
  const [routing, setRouting] = useState<{
    underwrtingEmails: string[];
    defaultCcEmails: string[];
    replyToEmail: string;
    notifyOnSubmit: boolean;
    notifyOnUpdate: boolean;
  }>({
    underwrtingEmails: [],
    defaultCcEmails: [],
    replyToEmail: "",
    notifyOnSubmit: true,
    notifyOnUpdate: false,
  });

  const { data: routingData } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "submission-routing"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/submission-routing`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!gaId,
  });

  useEffect(() => {
    if (routingData) {
      setRouting({
        underwrtingEmails: routingData.underwrtingEmails || [],
        defaultCcEmails: routingData.defaultCcEmails || [],
        replyToEmail: routingData.replyToEmail || "",
        notifyOnSubmit: routingData.notifyOnSubmit ?? true,
        notifyOnUpdate: routingData.notifyOnUpdate ?? false,
      });
    }
  }, [routingData]);

  const { data: brokers = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-links"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: assignments = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-assignments"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-assignments`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const saveRoutingMutation = useMutation({
    mutationFn: async (data: any) =>
      apiRequest("PUT", `/api/ga/${gaId}/submission-routing`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submission-routing"] });
      toast({ title: "Routing settings saved" });
    },
    onError: () =>
      toast({ title: "Error", description: "Failed to save routing settings.", variant: "destructive" }),
  });

  const saveAssignmentMutation = useMutation({
    mutationFn: async (data: { brokerAccountId: string; salesRepName: string; salesRepEmail: string }) =>
      apiRequest("PUT", `/api/ga/${gaId}/broker-assignments/${data.brokerAccountId}`, {
        salesRepName: data.salesRepName,
        salesRepEmail: data.salesRepEmail,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "broker-assignments"] });
      toast({ title: "Sales rep assigned" });
    },
    onError: () =>
      toast({ title: "Error", description: "Failed to save assignment.", variant: "destructive" }),
  });

  const addEmail = (type: "underwriting" | "cc") => {
    const raw = type === "underwriting" ? emailInput.trim() : ccEmailInput.trim();
    if (!raw || !raw.includes("@")) return;
    if (type === "underwriting") {
      if (!routing.underwrtingEmails.includes(raw)) {
        setRouting((r) => ({ ...r, underwrtingEmails: [...r.underwrtingEmails, raw] }));
      }
      setEmailInput("");
    } else {
      if (!routing.defaultCcEmails.includes(raw)) {
        setRouting((r) => ({ ...r, defaultCcEmails: [...r.defaultCcEmails, raw] }));
      }
      setCcEmailInput("");
    }
  };

  const removeEmail = (type: "underwriting" | "cc", email: string) => {
    if (type === "underwriting") {
      setRouting((r) => ({ ...r, underwrtingEmails: r.underwrtingEmails.filter((e) => e !== email) }));
    } else {
      setRouting((r) => ({ ...r, defaultCcEmails: r.defaultCcEmails.filter((e) => e !== email) }));
    }
  };

  const activeBrokers = brokers.filter((b: any) => b.status !== "license_revoked");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Case Submission Routing</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configure where broker case submissions are distributed within your team
        </p>
      </div>

      {/* How it works */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4 flex gap-3">
        <Info className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <p className="font-medium">How case submissions flow</p>
          <p className="text-blue-700 dark:text-blue-400">
            When a broker submits a case through Administr, it is automatically distributed to your
            underwriting team emails. The broker's assigned sales rep is CC'd so they stay in the loop.
          </p>
        </div>
      </div>

      {/* Underwriting team distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <Send className="w-4 h-4" /> Underwriting Team Distribution
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Underwriting Team Emails</Label>
            <p className="text-xs text-muted-foreground">
              Every case submission from any broker will be sent to all addresses listed here.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="underwriting@agency.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail("underwriting"))}
              />
              <Button variant="outline" onClick={() => addEmail("underwriting")}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {routing.underwrtingEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {routing.underwrtingEmails.map((email) => (
                  <EmailChip
                    key={email}
                    email={email}
                    onRemove={() => removeEmail("underwriting", email)}
                  />
                ))}
              </div>
            )}
            {routing.underwrtingEmails.length === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5 pt-1">
                <Info className="w-3.5 h-3.5" />
                No underwriting emails configured — submissions will not be distributed
              </p>
            )}
          </div>

          <div className="pt-2 border-t border-border space-y-2">
            <Label>Additional CC Emails (all submissions)</Label>
            <p className="text-xs text-muted-foreground">
              These addresses are CC'd on every submission, regardless of broker assignment.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="manager@agency.com"
                value={ccEmailInput}
                onChange={(e) => setCcEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail("cc"))}
              />
              <Button variant="outline" onClick={() => addEmail("cc")}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {routing.defaultCcEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {routing.defaultCcEmails.map((email) => (
                  <EmailChip
                    key={email}
                    email={email}
                    onRemove={() => removeEmail("cc", email)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-border space-y-2">
            <Label>Reply-To Email</Label>
            <p className="text-xs text-muted-foreground">Underwriters can reply directly to this address.</p>
            <Input
              type="email"
              placeholder="submissions@agency.com"
              className="max-w-sm"
              value={routing.replyToEmail}
              onChange={(e) => setRouting((r) => ({ ...r, replyToEmail: e.target.value }))}
            />
          </div>

          <div className="pt-2 border-t border-border space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Notification Triggers</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Notify on new submission</p>
                <p className="text-xs text-muted-foreground">Send email when a broker submits a new case</p>
              </div>
              <Switch
                checked={routing.notifyOnSubmit}
                onCheckedChange={(v) => setRouting((r) => ({ ...r, notifyOnSubmit: v }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Notify on submission update</p>
                <p className="text-xs text-muted-foreground">Send email when a broker updates an existing case</p>
              </div>
              <Switch
                checked={routing.notifyOnUpdate}
                onCheckedChange={(v) => setRouting((r) => ({ ...r, notifyOnUpdate: v }))}
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveRoutingMutation.mutate(routing)}
              disabled={saveRoutingMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {saveRoutingMutation.isPending ? "Saving…" : "Save Routing Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-broker sales rep assignment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <UserCheck className="w-4 h-4" /> Broker Sales Rep Assignments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <p className="text-xs text-muted-foreground px-4 py-2 border-b border-border">
            The assigned sales rep is automatically CC'd when their broker submits a case.
          </p>
          {activeBrokers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No active brokers in your network yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Broker</th>
                  <th className="text-left px-4 py-3">Assigned Sales Rep</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {activeBrokers.map((broker: any) => {
                  const assignment = assignments.find(
                    (a: any) => a.brokerAccountId === broker.brokerAccountId
                  );
                  return (
                    <BrokerRepRow
                      key={broker.brokerAccountId}
                      broker={broker}
                      assignment={assignment}
                      onSave={(brokerAccountId, salesRepName, salesRepEmail) =>
                        saveAssignmentMutation.mutate({ brokerAccountId, salesRepName, salesRepEmail })
                      }
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
